import { useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, STATES, INDUSTRIES } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import DocumentTemplateEditor from "../components/DocumentTemplateEditor";

const SETTINGS_SECTIONS = [
  ["profile", "Business Profile"],
  ["bank", "Bank & UPI"],
  ["invoiceSeries", "Invoice Series"],
  ["email", "Invoice Email"],
  ["templates", "Document Templates"],
  ["fields", "Custom Invoice Fields"],
  ["columns", "Invoice Columns"],
  ["branches", "Branches"],
  ["backup", "Backup & Restore"],
  ["security", "Change Password"],
];

export default function Settings() {
  const [activeSection, setActiveSection] = useState("profile");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [canAddBranches, setCanAddBranches] = useState(false);
  const [branchPlanName, setBranchPlanName] = useState("");
  const [branchLimit, setBranchLimit] = useState(0);
  const [additionalBranches, setAdditionalBranches] = useState(0);
  const [canUseCustomSender, setCanUseCustomSender] = useState(false);
  const [smtp, setSmtp] = useState({ configured: false, host: "", port: 587, secure: false, username: "", from_email: "", from_name: "", password: "" });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpAction, setSmtpAction] = useState("");
  const [branchName, setBranchName] = useState("");
  const [branchSaving, setBranchSaving] = useState(false);
  const [backupAction, setBackupAction] = useState("");
  const [restoreFile, setRestoreFile] = useState(null);

  useEffect(() => {
    API.get("/settings")
      .then((r) => setForm({ industry: "general", invoice_prefix: "INV-", col_item_no: true, col_boxes: false, col_retail_price: false, col_mrp: false, drug_license: "", custom_fields: [], ...r.data }))
      .catch((e) => toast.error(fmtErr(e)));
    API.get("/branches")
      .then(({ data }) => {
        setBranches(data.branches || []);
        setCanAddBranches(!!data.can_add_branches);
        setBranchPlanName(data.plan_name || "");
        setBranchLimit(data.branch_limit || 0);
        setAdditionalBranches(data.additional_branches || 0);
      })
      .catch((e) => toast.error(fmtErr(e)));
    API.get("/billing/my-subscription")
      .then(async ({ data }) => {
        const allowed = !!data.plan?.features?.custom_invoice_sender;
        setCanUseCustomSender(allowed);
        if (allowed) {
          const { data: config } = await API.get("/settings/invoice-smtp");
          setSmtp({ ...config, password: "" });
        }
      })
      .catch((e) => toast.error(fmtErr(e)));
  }, []);

  if (!form) return <p className="p-8 text-sm text-muted-foreground" data-testid="settings-loading">Loading…</p>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const changePassword = async () => {
    if (!pw.current || !pw.next) return toast.error("Fill both password fields");
    if (pw.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.next !== pw.confirm) return toast.error("New passwords do not match");
    setPwSaving(true);
    try {
      await API.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed successfully");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setPwSaving(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/settings", {
        business_name: form.business_name,
        gstin: form.gstin,
        state: form.state,
        address: form.address,
        phone: form.phone,
        email: form.email,
        invoice_prefix: String(form.invoice_prefix ?? "INV-").trim().toUpperCase(),
        bank_name: form.bank_name || "",
        bank_account: form.bank_account || "",
        bank_ifsc: form.bank_ifsc || "",
        upi_id: form.upi_id || "",
        industry: form.industry || "general",
        col_item_no: !!form.col_item_no,
        col_boxes: !!form.col_boxes,
        col_retail_price: !!form.col_retail_price,
        col_mrp: !!form.col_mrp,
        drug_license: form.drug_license || "",
        custom_fields: (form.custom_fields || []).filter((cf) => cf.label.trim() && cf.value.trim()),
      });
      setForm((current) => ({ ...current, invoice_prefix: data.invoice_prefix }));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try {
      const { data } = await API.put("/settings/invoice-smtp", {
        host: smtp.host,
        port: Number(smtp.port),
        secure: !!smtp.secure,
        username: smtp.username,
        ...(smtp.password ? { password: smtp.password } : {}),
        from_email: smtp.from_email,
        from_name: smtp.from_name,
      });
      setSmtp((current) => ({ ...data, password: "" }));
      toast.success("Invoice SMTP settings saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSmtpSaving(false);
    }
  };

  const testSmtp = async () => {
    setSmtpAction("test");
    try {
      const { data } = await API.post("/settings/invoice-smtp/test");
      toast.success(`Test email sent to ${data.emailed_to}`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSmtpAction("");
    }
  };

  const removeSmtp = async () => {
    if (!window.confirm("Remove the invoice SMTP configuration? Invoice emails will use the default email service.")) return;
    setSmtpAction("remove");
    try {
      await API.delete("/settings/invoice-smtp");
      setSmtp({ configured: false, host: "", port: 587, secure: false, username: "", from_email: "", from_name: "", password: "" });
      toast.success("Invoice SMTP configuration removed");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSmtpAction("");
    }
  };

  const updateDocumentTemplate = (documentType, template) => {
    setForm((current) => ({
      ...current,
      document_templates: { ...(current.document_templates || {}), [documentType]: template },
    }));
  };

  const addBranch = async () => {
    if (branchName.trim().length < 2) return toast.error("Branch name must contain at least 2 characters");
    setBranchSaving(true);
    try {
      const { data } = await API.post("/branches", { name: branchName.trim() });
      window.localStorage.setItem("comfy-ledger-branch-id", data.id);
      window.location.reload();
    } catch (e) {
      toast.error(fmtErr(e));
      setBranchSaving(false);
    }
  };

  const downloadBackup = async () => {
    setBackupAction("download");
    try {
      const { data } = await API.get("/backup/download", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `comfy-ledger-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBackupAction("");
    }
  };

  const emailBackup = async () => {
    setBackupAction("email");
    try {
      const { data } = await API.post("/backup/email");
      toast.success(`Backup emailed to ${data.email}`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBackupAction("");
    }
  };

  const restoreBackup = async () => {
    if (!restoreFile) return toast.error("Choose a backup file first");
    if (!window.confirm("Restore this backup and replace all current account business data across every branch? This cannot be undone.")) return;
    setBackupAction("restore");
    try {
      const body = new FormData();
      body.append("file", restoreFile);
      body.append("confirm", "true");
      await API.post("/backup/restore", body);
      toast.success("Backup restored successfully");
      setRestoreFile(null);
      window.localStorage.setItem("comfy-ledger-branch-id", "main");
      window.location.reload();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBackupAction("");
    }
  };

  return (
    <div className="max-w-6xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Business Settings</h1>
        <p className="text-sm text-muted-foreground">
          Choose a settings section below. Your state determines CGST/SGST vs IGST, and GSTIN appears on GSTR exports.
        </p>
      </div>
      <nav
        className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        aria-label="Settings sections"
        data-testid="settings-section-navigation"
      >
        {SETTINGS_SECTIONS.map(([section, label]) => (
          <Button
            key={section}
            type="button"
            variant={activeSection === section ? "default" : "outline"}
            className="h-auto min-h-11 whitespace-normal px-3 py-2 text-center"
            onClick={() => setActiveSection(section)}
            aria-current={activeSection === section ? "page" : undefined}
            data-testid={`settings-section-${section}`}
          >
            {label}
          </Button>
        ))}
      </nav>
      <div className="space-y-4 rounded-lg border bg-card p-6">
        {activeSection === "profile" && (
          <>
        <div className="space-y-2">
          <Label>Business Name</Label>
          <Input value={form.business_name} onChange={set("business_name")} data-testid="settings-business-name-input" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>GSTIN</Label>
            <Input value={form.gstin} onChange={set("gstin")} placeholder="22AAAAA0000A1Z5" data-testid="settings-gstin-input" />
          </div>
          <div className="space-y-2">
            <Label>State (Place of Business)</Label>
            <Select value={form.state} onValueChange={set("state")}>
              <SelectTrigger data-testid="settings-state-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Industry</Label>
            <Select value={form.industry || "general"} onValueChange={set("industry")}>
              <SelectTrigger data-testid="settings-industry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.industry === "pharma" && (
              <p className="text-xs text-muted-foreground" data-testid="settings-pharma-hint">Pharma: Batch No, Mfg Date &amp; Expiry Date fields appear at billing and on PDFs.</p>
            )}
          </div>
          {form.industry === "pharma" && (
            <div className="space-y-2">
              <Label>Drug Licence No. (optional)</Label>
              <Input value={form.drug_license || ""} onChange={set("drug_license")} placeholder="e.g. MH-PUN-123456" data-testid="settings-drug-license-input" />
              <p className="text-xs text-muted-foreground">Printed on invoices below your GSTIN.</p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={set("phone")} data-testid="settings-phone-input" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={set("email")} data-testid="settings-email-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Address</Label>
          <Textarea value={form.address} onChange={set("address")} data-testid="settings-address-input" />
        </div>
          </>
        )}
        {activeSection === "bank" && (
        <div className="space-y-4 border-t pt-4">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">Bank &amp; UPI</h2>
            <p className="text-xs text-muted-foreground">
              Printed on estimate and sales invoice PDFs.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Bank Name</Label>
              <Input value={form.bank_name || ""} onChange={set("bank_name")} placeholder="HDFC Bank, Pune" data-testid="settings-bank-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Account Number</Label>
              <Input value={form.bank_account || ""} onChange={set("bank_account")} data-testid="settings-account-input" />
            </div>
            <div className="space-y-2">
              <Label>IFSC</Label>
              <Input value={form.bank_ifsc || ""} onChange={set("bank_ifsc")} placeholder="HDFC0000123" data-testid="settings-ifsc-input" />
            </div>
            <div className="space-y-2">
              <Label>UPI ID</Label>
              <Input value={form.upi_id || ""} onChange={set("upi_id")} placeholder="business@okhdfc" data-testid="settings-upi-input" />
            </div>
          </div>
        </div>
        )}
        {activeSection === "invoiceSeries" && (
          <div className="space-y-4 border-t pt-4" data-testid="invoice-series-settings">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight">Invoice series</h2>
              <p className="text-xs text-muted-foreground">
                Set the prefix for new sales invoices. Existing invoices and other document series are not changed.
              </p>
            </div>
            <div className="max-w-md space-y-2">
              <Label htmlFor="settings-invoice-prefix">Invoice number prefix</Label>
              <Input
                id="settings-invoice-prefix"
                value={form.invoice_prefix ?? "INV-"}
                onChange={set("invoice_prefix")}
                maxLength={24}
                pattern="[A-Za-z0-9][A-Za-z0-9._/-]{0,23}"
                data-testid="settings-invoice-prefix-input"
              />
              <p className="text-xs text-muted-foreground">
                Up to 24 letters, numbers, hyphens, slashes, dots, or underscores. The 4-digit sequence is added after the prefix (for example, INV-0001).
              </p>
            </div>
          </div>
        )}
        {activeSection === "email" && (
          <div className="space-y-4 border-t pt-4" data-testid="invoice-smtp-section">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight">Invoice Sender (SMTP)</h2>
              <p className="text-xs text-muted-foreground">
                Invoice emails use this SMTP account when configured. SMTP credentials are encrypted and are never returned to the browser.
              </p>
            </div>
            {!canUseCustomSender ? (
              <p className="rounded-md border bg-muted/20 p-4 text-sm text-muted-foreground" data-testid="invoice-smtp-plan-note">
                Custom invoice SMTP is included with eligible paid subscription plans. Upgrade to configure a sender.
              </p>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="invoice-smtp-host">SMTP host</Label>
                    <Input id="invoice-smtp-host" value={smtp.host || ""} onChange={(e) => setSmtp((s) => ({ ...s, host: e.target.value }))} placeholder="smtp.example.com" data-testid="invoice-smtp-host-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice-smtp-port">Port</Label>
                    <Input id="invoice-smtp-port" type="number" min="1" max="65535" value={smtp.port || 587} onChange={(e) => setSmtp((s) => ({ ...s, port: e.target.value }))} data-testid="invoice-smtp-port-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice-smtp-username">SMTP username (optional)</Label>
                    <Input id="invoice-smtp-username" autoComplete="username" value={smtp.username || ""} onChange={(e) => setSmtp((s) => ({ ...s, username: e.target.value }))} data-testid="invoice-smtp-username-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice-smtp-password">SMTP password (optional)</Label>
                    <Input id="invoice-smtp-password" type="password" autoComplete="new-password" value={smtp.password || ""} onChange={(e) => setSmtp((s) => ({ ...s, password: e.target.value }))} placeholder={smtp.password_set ? "Saved; leave blank to keep current password" : "SMTP password or app password"} data-testid="invoice-smtp-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice-smtp-from-email">From email</Label>
                    <Input id="invoice-smtp-from-email" type="email" value={smtp.from_email || ""} onChange={(e) => setSmtp((s) => ({ ...s, from_email: e.target.value }))} placeholder="invoices@yourdomain.com" data-testid="invoice-smtp-from-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice-smtp-from-name">Sender name (optional)</Label>
                    <Input id="invoice-smtp-from-name" value={smtp.from_name || ""} onChange={(e) => setSmtp((s) => ({ ...s, from_name: e.target.value }))} placeholder={form.business_name || "Your business"} data-testid="invoice-smtp-from-name-input" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm" htmlFor="invoice-smtp-secure">
                  <Checkbox id="invoice-smtp-secure" checked={!!smtp.secure} onCheckedChange={(checked) => setSmtp((s) => ({ ...s, secure: checked === true }))} data-testid="invoice-smtp-secure-checkbox" />
                  Use implicit TLS (usually port 465; port 587 uses STARTTLS)
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={saveSmtp} disabled={smtpSaving} data-testid="invoice-smtp-save-button">{smtpSaving ? "Saving…" : "Save SMTP Settings"}</Button>
                  <Button variant="outline" onClick={testSmtp} disabled={!smtp.configured || !!smtpAction} data-testid="invoice-smtp-test-button">{smtpAction === "test" ? "Sending…" : "Send Test Email"}</Button>
                  {smtp.configured && <Button variant="destructive" onClick={removeSmtp} disabled={!!smtpAction} data-testid="invoice-smtp-remove-button">{smtpAction === "remove" ? "Removing…" : "Remove SMTP"}</Button>}
                </div>
              </>
            )}
          </div>
        )}
        {activeSection === "templates" && (
        <DocumentTemplateEditor
          templates={form.document_templates}
          onSaved={updateDocumentTemplate}
        />
        )}
        {activeSection === "fields" && (
        <div className="space-y-4 border-t pt-4" data-testid="custom-fields-section">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">Custom Invoice Fields</h2>
            <p className="text-xs text-muted-foreground">
              Add business registrations — Drug Licence, FSSAI No., Udyam, CIN, etc. They print on your invoice/bill PDFs (up to 6).
            </p>
          </div>
          {(form.custom_fields || []).map((cf, i) => (
            <div key={i} className="flex gap-2" data-testid={`custom-field-row-${i}`}>
              <Input
                className="w-2/5" placeholder="Label (e.g. FSSAI No.)" value={cf.label}
                onChange={(e) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) }))}
                data-testid={`custom-field-label-${i}`}
              />
              <Input
                placeholder="Value" value={cf.value}
                onChange={(e) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) }))}
                data-testid={`custom-field-value-${i}`}
              />
              <Button
                variant="ghost" size="icon"
                onClick={() => setForm((f) => ({ ...f, custom_fields: f.custom_fields.filter((_, j) => j !== i) }))}
                data-testid={`custom-field-remove-${i}`}
              >
                <Trash size={15} className="text-muted-foreground" />
              </Button>
            </div>
          ))}
          {(form.custom_fields || []).length < 6 && (
            <Button
              variant="outline" size="sm"
              onClick={() => setForm((f) => ({ ...f, custom_fields: [...(f.custom_fields || []), { label: "", value: "" }] }))}
              data-testid="custom-field-add-button"
            >
              <Plus size={14} className="mr-1" /> Add Field
            </Button>
          )}
        </div>
        )}

        {activeSection === "columns" && (
        <div className="space-y-4 border-t pt-4" data-testid="invoice-columns-section">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">Invoice Columns</h2>
            <p className="text-xs text-muted-foreground">
              Choose which extra columns appear on bill/invoice entry and PDFs. Batch / Mfg / Exp columns are auto-enabled for the Pharma industry.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["col_item_no", "Item Sr. No.", "settings-col-item-no-check"],
              ["col_boxes", "No. of Boxes", "settings-col-boxes-check"],
              ["col_retail_price", "Retail Price", "settings-col-retail-check"],
              ["col_mrp", "MRP", "settings-col-mrp-check"],
            ].map(([key, label, testid]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!form[key]} onCheckedChange={(v) => setForm((f) => ({ ...f, [key]: !!v }))} data-testid={testid} />
                {label}
              </label>
            ))}
          </div>
        </div>
        )}
        {["profile", "bank", "invoiceSeries", "fields", "columns"].includes(activeSection) && (
          <Button onClick={submit} disabled={saving} data-testid="settings-save-button">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        )}
      </div>

      {activeSection === "branches" && (
      <div className="space-y-4 rounded-lg border bg-card p-6" data-testid="branches-card">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-tight">Branches</h2>
          <p className="text-xs text-muted-foreground">
            Each branch has separate invoices, parties, inventory, banking, business settings, and reports. Existing records stay in Main Branch.
          </p>
          <p className="text-sm font-medium" data-testid="branch-allowance">
            Additional branches: {additionalBranches} / {branchLimit}
          </p>
        </div>
        {canAddBranches ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              placeholder="New branch name"
              aria-label="New branch name"
              data-testid="branch-name-input"
            />
            <Button onClick={addBranch} disabled={branchSaving} data-testid="branch-add-button">
              <Plus size={16} className="mr-1" />
              {branchSaving ? "Adding…" : "Add Branch"}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="branch-plan-message">
            {branchLimit === 0
              ? `Adding branches is not included in your ${branchPlanName || "current"} plan. Existing branches remain accessible.`
              : `You have reached the ${branchPlanName || "current"} plan limit of ${branchLimit} additional branches. Existing branches remain accessible.`}
          </p>
        )}
        <ul className="space-y-1 text-sm text-muted-foreground" data-testid="branch-list">
          {branches.map((branch) => (
            <li key={branch.id}>{branch.name}{branch.is_default ? " (default)" : ""}</li>
          ))}
        </ul>
      </div>
      )}

      {activeSection === "backup" && (
      <div className="space-y-4 rounded-lg border bg-card p-6" data-testid="backup-card">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-tight">Backup &amp; Restore</h2>
          <p className="text-xs text-muted-foreground">
            Backups include your business data and all branches, but exclude sign-in credentials and subscription settings. Restore replaces that data across every branch.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={downloadBackup}
            disabled={!!backupAction}
            data-testid="backup-download-button"
          >
            {backupAction === "download" ? "Preparing…" : "Download Backup"}
          </Button>
          <Button
            variant="outline"
            onClick={emailBackup}
            disabled={!!backupAction}
            data-testid="backup-email-button"
          >
            {backupAction === "email" ? "Sending…" : "Email Backup"}
          </Button>
        </div>
        <div className="space-y-3 border-t pt-4">
          <div className="space-y-2">
            <Label htmlFor="settings-backup-file">Upload backup file</Label>
            <Input
              id="settings-backup-file"
              type="file"
              accept=".json,application/json"
              disabled={!!backupAction}
              onChange={(event) => setRestoreFile(event.target.files?.[0] || null)}
              data-testid="backup-file-input"
            />
            <p className="text-xs text-muted-foreground">
              Restore is limited to a backup created for this account. Maximum file size: 25 MB.
            </p>
          </div>
          <Button
            variant="destructive"
            onClick={restoreBackup}
            disabled={!restoreFile || !!backupAction}
            data-testid="backup-restore-button"
          >
            {backupAction === "restore" ? "Restoring…" : "Restore Backup"}
          </Button>
        </div>
      </div>
      )}

      {activeSection === "security" && (
      <div className="space-y-4 rounded-lg border bg-card p-6" data-testid="change-password-card">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-tight">Change Password</h2>
          <p className="text-xs text-muted-foreground">For email/password accounts. Google sign-in accounts don't have a password.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>Current Password</Label>
            <Input type="password" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} data-testid="pw-current-input" />
          </div>
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input type="password" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} data-testid="pw-new-input" />
          </div>
          <div className="space-y-2">
            <Label>Confirm New Password</Label>
            <Input type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="pw-confirm-input" />
          </div>
        </div>
        <Button variant="outline" onClick={changePassword} disabled={pwSaving} data-testid="change-password-button">{pwSaving ? "Changing…" : "Change Password"}</Button>
      </div>
      )}
    </div>
  );
}
